import { useEffect, useState } from "react";
import { Loader2, Check, Star, Bookmark, BookmarkCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, searchUSDA, searchSwiss } from "../lib/api";
import { titleCase } from "../lib/nutrition";

const ORDER = { Foundation: 0, "SR Legacy": 1, "Survey (FNDDS)": 2, Branded: 3 };

const FoodRow = ({ food, onClick, isMemorized, onMemorize, memorizeBusy }) => {
  const isBLV = food._source === "BLV";
  const refTag = isBLV ? `BLV #${food.blv_dbid}` : `FDC #${food.fdc_id}`;
  const flag = isBLV ? "🇨🇭" : "🇺🇸";
  const rowKey = isBLV ? food.blv_dbid : food.fdc_id;
  return (
    <div
      className={`w-full text-left px-6 py-3 border-b border-stone-100 last:border-0 transition-colors flex items-start gap-3 ${
        isMemorized ? "bg-emerald-50/60" : "hover:bg-emerald-50"
      }`}
      data-testid={`usda-picker-result-${rowKey}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 text-left group flex items-start gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isMemorized && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold tracking-wider uppercase"
                data-testid={`usda-memorized-badge-${rowKey}`}
              >
                <Star className="h-2.5 w-2.5 fill-current" />
                Mémorisé
              </span>
            )}
            <span className="font-semibold text-stone-900 leading-tight">
              {isBLV ? food.description : titleCase(food.description)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-stone-500 mt-0.5 flex-wrap">
            <span>{flag}</span>
            {isBLV ? (
              food.category && (
                <span className="uppercase tracking-wider font-bold">{food.category}</span>
              )
            ) : (
              <>
                {food.brand && <span className="truncate max-w-[200px]">{titleCase(food.brand)}</span>}
                {food.brand && <span>·</span>}
                <span className="uppercase tracking-wider font-bold">{food.data_type}</span>
              </>
            )}
            <span>·</span>
            <span className="font-mono">{refTag}</span>
          </div>
          <div className="text-xs text-stone-600 mt-1.5 flex items-center gap-3 flex-wrap">
            <span className="font-bold text-emerald-700 tabular-nums">
              {food.nutrients_per_100g.calories} kcal
            </span>
            <span className="tabular-nums">P {food.nutrients_per_100g.protein}g</span>
            <span className="tabular-nums">G {food.nutrients_per_100g.carbs}g</span>
            <span className="tabular-nums">L {food.nutrients_per_100g.fat}g</span>
            <span className="tabular-nums">F {food.nutrients_per_100g.fiber}g</span>
            <span className="text-stone-400">/ 100 g</span>
          </div>
        </div>
        <span className="shrink-0 h-8 w-8 rounded-full bg-stone-100 group-hover:bg-emerald-600 group-hover:text-white text-stone-500 grid place-items-center transition-colors">
          <Check className="h-4 w-4" />
        </span>
      </button>
      {onMemorize && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onMemorize}
          disabled={memorizeBusy}
          className={`h-8 w-8 shrink-0 ${
            isMemorized
              ? "text-emerald-700 hover:bg-emerald-100"
              : "text-stone-300 hover:text-emerald-700 hover:bg-emerald-50"
          }`}
          title={
            isMemorized
              ? "Choix mémorisé — cliquez pour mettre à jour avec cet ingrédient"
              : "Mémoriser comme correspondance par défaut"
          }
          data-testid={`memorize-btn-${rowKey}`}
        >
          {isMemorized ? (
            <BookmarkCheck className="h-4 w-4 fill-current" />
          ) : (
            <Bookmark className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
};

export const USDAPickerDialog = ({
  open,
  onOpenChange,
  initialQuery = "",
  initialQueryFr = "",
  suggestionLabel = "",
  onPick,
}) => {
  const [source, setSource] = useState("BLV");
  const [query, setQuery] = useState(initialQueryFr || initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memorizedFood, setMemorizedFood] = useState(null);
  const [memorizeBusyFdc, setMemorizeBusyFdc] = useState(null);

  const isBLV = source === "BLV";

  useEffect(() => {
    if (!open) return;
    // Initialize source based on whether a French query is available
    const startSource = initialQueryFr ? "BLV" : "USDA";
    setSource(startSource);
    setQuery(startSource === "BLV" ? initialQueryFr : initialQuery);
    setMemorizedFood(null);
    // Lookup memorized food using the original (English) query as the key
    if (initialQuery?.trim()) {
      api
        .get("/integration/lexicon", { params: { query: initialQuery.trim() } })
        .then(({ data }) => {
          setMemorizedFood({
            fdc_id: data.fdc_id,
            description: data.description,
            brand: data.brand,
            data_type: data.data_type,
            nutrients_per_100g: data.nutrients_per_100g,
            _source: "USDA",
          });
        })
        .catch(() => setMemorizedFood(null));
    }
  }, [open, initialQuery, initialQueryFr]);

  useEffect(() => {
    if (!open || !query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const fetcher = isBLV ? searchSwiss : searchUSDA;
    fetcher(query.trim(), 15)
      .then((data) => {
        if (cancelled) return;
        if (isBLV) {
          // Adapt Swiss results to the FoodRow shape
          setResults(
            data.map((f) => ({
              fdc_id: null,
              blv_dbid: f.blv_dbid,
              description: f.description,
              brand: null,
              data_type: "BLV",
              nutrients_per_100g: f.nutrients_per_100g,
              category: f.category,
              _source: "BLV",
            }))
          );
        } else {
          const sorted = [...data].sort(
            (a, b) => (ORDER[a.data_type] ?? 9) - (ORDER[b.data_type] ?? 9)
          );
          setResults(sorted.map((f) => ({ ...f, _source: "USDA" })));
        }
      })
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, query, isBLV]);

  const memorize = async (food) => {
    if (!query.trim()) return;
    setMemorizeBusyFdc(food.fdc_id);
    try {
      await api.post("/integration/lexicon", {
        query: query.trim(),
        fdc_id: food.fdc_id,
        description: food.description,
        brand: food.brand,
        data_type: food.data_type,
        nutrients_per_100g: food.nutrients_per_100g,
      });
      setMemorizedFood(food);
    } catch {
      // silent fail
    } finally {
      setMemorizeBusyFdc(null);
    }
  };

  // Exclude memorized from main results to avoid duplicate display (USDA only)
  const filteredResults = memorizedFood && !isBLV
    ? results.filter((r) => r.fdc_id !== memorizedFood.fdc_id)
    : results;

  const resultKey = (food) =>
    food._source === "BLV" ? `blv-${food.blv_dbid}` : `usda-${food.fdc_id}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl p-0 overflow-hidden gap-0"
        data-testid="usda-picker-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="font-heading text-lg">
            Choisir le bon ingrédient
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {suggestionLabel ? (
              <>
                Suggestion IA : <span className="font-semibold text-stone-700">{suggestionLabel}</span>{" "}
                — vérifiez la correspondance avant d&apos;ajouter.
              </>
            ) : (
              "Sélectionnez la variante qui correspond à votre ingrédient."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-stone-100 bg-stone-50/60 space-y-3">
          <div
            className="flex items-center gap-1 p-1 bg-white border border-stone-200 rounded-lg w-fit"
            data-testid="picker-source-toggle"
          >
            <button
              type="button"
              onClick={() => {
                setSource("BLV");
                if (initialQueryFr) setQuery(initialQueryFr);
              }}
              className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors flex items-center gap-1.5 ${
                isBLV ? "bg-emerald-700 text-white" : "text-stone-500 hover:text-stone-700"
              }`}
              data-testid="picker-source-blv"
            >
              <span>🇨🇭</span>
              Suisse · FR
            </button>
            <button
              type="button"
              onClick={() => {
                setSource("USDA");
                setQuery(initialQuery);
              }}
              className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors flex items-center gap-1.5 ${
                !isBLV ? "bg-emerald-700 text-white" : "text-stone-500 hover:text-stone-700"
              }`}
              data-testid="picker-source-usda"
            >
              <span>🇺🇸</span>
              USDA · EN
            </button>
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              isBLV ? "Affiner la requête (français)…" : "Affiner la requête USDA (anglais)…"
            }
            className="border-stone-200 focus-visible:ring-emerald-500"
            data-testid="usda-picker-input"
            autoFocus
          />
          <p className="text-[11px] text-stone-500">
            💡 Reformulez si besoin.
            {!isBLV && (
              <>
                {" "}
                Cliquez <Bookmark className="h-3 w-3 inline -mt-0.5" /> pour mémoriser un choix USDA
                réutilisable.
              </>
            )}
          </p>
        </div>

        <div className="max-h-[440px] overflow-y-auto scrollbar-thin">
          {!isBLV && memorizedFood && (
            <FoodRow
              food={memorizedFood}
              onClick={() => onPick(memorizedFood)}
              isMemorized
              onMemorize={() => memorize(memorizedFood)}
              memorizeBusy={memorizeBusyFdc === memorizedFood.fdc_id}
            />
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 text-stone-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {isBLV ? "Recherche BLV…" : "Recherche USDA…"}
            </div>
          )}
          {!loading && filteredResults.length === 0 && !memorizedFood && query.trim() && (
            <div className="py-10 text-center text-sm text-stone-500" data-testid="usda-picker-empty">
              Aucun résultat. Reformulez votre requête.
            </div>
          )}
          {!loading &&
            filteredResults.map((food) => (
              <FoodRow
                key={resultKey(food)}
                food={food}
                onClick={() => onPick(food)}
                isMemorized={false}
                onMemorize={isBLV ? null : () => memorize(food)}
                memorizeBusy={memorizeBusyFdc === food.fdc_id}
              />
            ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-stone-100 bg-white">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-stone-600"
            data-testid="usda-picker-cancel"
          >
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default USDAPickerDialog;
